import React, { useState, useEffect } from "react";
import { useSwipeable } from "react-swipeable";
import styles from "./StackedSwipeView.module.css"; // reuse your CSS

interface StackedSwipeViewProps<T> {
    items: T[];
    triggerPrevSignal?: number;
    setEnablePrevButton?: (val: boolean) => void;
    renderCard: (item: T) => React.ReactNode;
    renderPlaceholder: () => React.ReactNode;
    onSwipeNext?: () => void;
}

function StackedSwipeView<T>({
    items,
    triggerPrevSignal = 0,
    setEnablePrevButton = () => { },
    renderCard,
    renderPlaceholder,
    onSwipeNext,
}: StackedSwipeViewProps<T>) {
    const [currentIndex, setCurrentIndex] = useState<number>(0);
    const [offsetX, setOffsetX] = useState<number>(0);
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [rotation, setRotation] = useState<number>(0);
    const [isVerticalScroll, setIsVerticalScroll] = useState(false);

    useEffect(() => {
        if (triggerPrevSignal > 0) {
            setCurrentIndex((prevIndex) => {
                const newIndex = Math.max(prevIndex - 1, 0);
                return newIndex;
            });

        }
    }, [triggerPrevSignal]);

    useEffect(() => {
        setEnablePrevButton(currentIndex !== 0);
    }, [currentIndex]);

    const numItems = items.length;
    const multiplier = 0.3;

    const handleTouchEnd = () => setIsVerticalScroll(false);
    const handleSwipeStart = () => setIsDragging(true);

    const handleSwipeMove = (deltaX: number, deltaY: number) => {
        if (!isDragging) return;
        if (Math.abs(deltaY) > Math.abs(deltaX) || isVerticalScroll) {
            setIsVerticalScroll(true);
            return;
        }
        setIsVerticalScroll(false);
        const maxOffset = 200;
        const newOffsetX = Math.max(-maxOffset, Math.min(maxOffset, deltaX * multiplier));
        setOffsetX(newOffsetX);
        setRotation((newOffsetX / maxOffset) * 15);
    };

    const handleSwipeEnd = (deltaX: number) => {
        setIsDragging(false);
        if (isVerticalScroll) return;

        const threshold = 100;
        const swipeLeft = deltaX < -threshold;
        const swipeRight = deltaX > threshold;

        if (swipeLeft || swipeRight) {
            const exitOffset = swipeLeft ? - (window.innerWidth + 100) : (window.innerWidth + 100);
            const finalRotation = swipeLeft ? -30 : 30;

            setOffsetX(exitOffset);
            setRotation(finalRotation);

            setTimeout(() => {
                setOffsetX(0);
                setRotation(0);
                setCurrentIndex((prev) => (prev + 1) % (numItems + 1));
                onSwipeNext?.();
            }, 300);
        } else {
            // Not enough swipe to dismiss
            setOffsetX(0);
            setRotation(0);
        }
    };

    const swipeHandlers = useSwipeable({
        onSwipeStart: handleSwipeStart,
        onSwiping: (e) => handleSwipeMove(e.deltaX, e.deltaY),
        onSwiped: (e) => handleSwipeEnd(e.deltaX),
        preventScrollOnSwipe: false,
    });

    const topItem = items[currentIndex];
    const nextItem = items[currentIndex + 1];

    return (
        <div className={styles.stackedSwipeView} {...swipeHandlers} onTouchEnd={handleTouchEnd}>
            {numItems > 0 && (
                <>
                    {/* Top card */}
                    <div
                        key={currentIndex}
                        className={`${styles.card} ${styles.topCard}`}
                        style={{
                            zIndex: 2,
                            transform: `translateX(${offsetX}px) rotate(${rotation}deg)`,
                            opacity: 1,
                            transition: isDragging ? "none" : "transform 0.3s ease-in-out, opacity 0.3s ease-in-out",
                        }}
                    >
                        {currentIndex < numItems ? renderCard(topItem) : renderPlaceholder()}
                    </div>

                    {/* Back card */}
                    <div
                        key={currentIndex + 1 <= numItems ? currentIndex + 1 : 0}
                        className={styles.card}
                        style={{
                            zIndex: 1,
                            transform: "translateY(10px) scale(0.95)",
                            opacity: 1,
                            transition: "transform 0.3s ease-in-out, opacity 0.3s ease-in-out",
                        }}
                    >
                        {currentIndex + 1 < numItems
                            ? renderCard(nextItem)
                            : currentIndex + 1 === numItems
                                ? renderPlaceholder()
                                : renderCard(items[0])}
                    </div>
                </>
            )}
            {numItems === 0 && (
                <div className={styles.card} style={{ zIndex: 1 }}>
                    {renderPlaceholder()}
                </div>
            )}
        </div>
    );
}

export default StackedSwipeView;
